import { commandStore } from './command-store';
import { getSharedSequenceManager } from './hotkey-singletons';
import type { Command } from './types';

/**
 * Track all registered hotkey sequences globally so we can detect duplicates.
 * Maps key string (e.g., "O+E") to the list of commands that registered it.
 */
const hotkeyOwners = new Map<string, Command[]>();

/**
 * Stores the single sequence-manager handle per unique key string.
 * Only one handler is registered per key sequence — it iterates owners
 * and fires the first enabled command.
 */
const hotkeyManagerHandles = new Map<string, { unregister: () => void }>();

/**
 * Non-reactive map holding the latest command callbacks.
 * Hotkey handlers read from here instead of the reactive store so that
 * callback updates don't trigger unnecessary re-renders.
 */
const commandHandlers = new Map<string, Command>();

const registerHotkey = (command: Command) => {
    if (command.keys.length === 0) {
        return;
    }

    const keyString = command.keys.join('+');
    const existing = hotkeyOwners.get(keyString);

    if (existing) {
        const allGuarded = existing.every((c) => c.enabled !== undefined) && command.enabled !== undefined;
        if (!allGuarded) {
            console.warn(
                `[command-menu] Duplicate shortcut "${command.keys.join(' ')}" — command "${command.id}" was skipped. Already registered by "${existing.map((c) => c.id).join(', ')}".`
            );
            return;
        }
        existing.push(command);
        // No new manager.register() — the shared handler already covers this command.
    } else {
        hotkeyOwners.set(keyString, [command]);

        // Register ONE handler per unique key sequence. It walks the owners
        // list and fires only the first command whose enabled() returns true.
        const manager = getSharedSequenceManager();
        const handle = manager.register(
            [...command.keys],
            () => {
                const owners = hotkeyOwners.get(keyString);
                if (!owners) {
                    return;
                }
                for (const owner of owners) {
                    const latest = commandHandlers.get(owner.id);
                    if (!latest) {
                        continue;
                    }
                    if (latest.enabled !== undefined && !latest.enabled()) {
                        continue;
                    }
                    commandStore.setState((previous) => ({ isOpen: false, commands: previous.commands }));
                    latest.onSelect('hotkey');
                    queueMicrotask(() => {
                        manager.resetAll();
                    });
                    return;
                }
            },
            { conflictBehavior: 'allow' }
        );
        hotkeyManagerHandles.set(keyString, handle);
    }
};

const unregisterHotkey = (command: Command) => {
    const keyString = command.keys.join('+');
    const owners = hotkeyOwners.get(keyString);
    if (!owners) {
        return;
    }
    const index = owners.findIndex((c) => c.id === command.id);
    if (index !== -1) {
        owners.splice(index, 1);
    }
    if (owners.length === 0) {
        hotkeyOwners.delete(keyString);
        const handle = hotkeyManagerHandles.get(keyString);
        if (handle) {
            handle.unregister();
            hotkeyManagerHandles.delete(keyString);
        }
    }
};

/**
 * Register commands in the global command menu.
 * Hotkeys are bound immediately — no UI component needed.
 * Returns an unregister function that removes the commands and their hotkeys.
 *
 * @example
 * const unregister = registerCommands([
 *   { id: 'ot:cancel', keys: ['G', 'X'], description: 'Cancel order', onSelect: () => openCancelModal() },
 * ]);
 *
 * // Later, when the context changes (e.g., component unmount):
 * unregister();
 */
export const registerCommands = (commands: Command[]): (() => void) => {
    const registered: string[] = [];

    commandStore.setState((previous) => {
        const next = new Map(previous.commands);
        for (const command of commands) {
            if (next.has(command.id)) {
                console.warn(`[command-menu] Duplicate command id "${command.id}" — skipped.`);
                continue;
            }
            next.set(command.id, command);
            commandHandlers.set(command.id, command);
            registered.push(command.id);
            registerHotkey(command);
        }
        return { isOpen: previous.isOpen, commands: next };
    });

    return () => {
        commandStore.setState((previous) => {
            const next = new Map(previous.commands);
            for (const id of registered) {
                const command = next.get(id);
                if (command) {
                    unregisterHotkey(command);
                }
                next.delete(id);
                commandHandlers.delete(id);
            }
            return { isOpen: previous.isOpen, commands: next };
        });
    };
};

/**
 * Check if a command is enabled using the latest closure from commandHandlers.
 * Returns true if the command has no enabled guard or the guard returns true.
 */
export const isCommandEnabled = (id: string): boolean => {
    const command = commandHandlers.get(id);
    if (command?.enabled === undefined) {
        return true;
    }
    return command.enabled();
};

/**
 * Get the latest registered command object for the given id.
 * Palette interactions use this to avoid stale closures after React re-renders.
 */
export const getRegisteredCommand = (id: string): Command | undefined => {
    return commandHandlers.get(id);
};

/**
 * Update already-registered commands with fresh objects (new closures).
 * Only touches the non-reactive commandHandlers map — the reactive store
 * is NOT updated, so UI consumers (useCommands) won't re-render.
 */
export const updateCommands = (commands: Command[]): void => {
    const seen = new Set<string>();
    for (const command of commands) {
        if (seen.has(command.id)) {
            continue;
        }
        seen.add(command.id);
        if (commandHandlers.has(command.id)) {
            commandHandlers.set(command.id, command);
        }
    }
};
